import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { getMediaUrl } from '../../services/media';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import styles from './BookForm.module.css';

const emptyForm = {
  title: '', author: '', year: '', language: '', description: '',
};

const LANGUAGE_OPTIONS = [
  { value: 'kz', label: 'Казахский' },
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'Английский' },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1899 }, (_, i) => {
  const year = CURRENT_YEAR - i;
  return { value: String(year), label: String(year) };
});

const BookForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [persons, setPersons] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [bookFile, setBookFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [allGenres, setAllGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);

  useEffect(() => {
    api.get('/persons').then((res) => {
      const data = Array.isArray(res.data) ? res.data : (res.data?.content || []);
      setPersons(data);
    }).catch(() => {});

    api.get('/genres').then((res) => {
      setAllGenres(res.data?.genres || []);
    }).catch(() => {});

    if (isEdit) {
      setLoading(true);
      api.get(`/books/${id}`).then((res) => {
        const b = res.data;
        setForm({
          title: b.title || '',
          author: b.author || '',
          year: b.year || '',
          language: b.language || '',
          description: b.description || '',
        });
        setSelectedGenres(b.genres || []);
        if (b.coverUrl) setImagePreview(getMediaUrl(b.coverUrl));
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) {
      setError('Название обязательно');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => formData.append(key, val));
      formData.append('source', 'manual');
      selectedGenres.forEach((g) => formData.append('genre_ids', g.id));
      if (imageFile) formData.append('cover', imageFile);
      if (bookFile) formData.append('file', bookFile);

      if (isEdit) {
        await api.put(`/books/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/books', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      navigate('/books');
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при сохранении. Проверьте данные.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Загрузка...</div>;

  return (
    <div className={styles.page}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.section}>
          <div className={styles.grid}>
            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Название *</label>
              <input className={styles.input} name="title" value={form.title} onChange={handleChange} required placeholder="Введите название книги" />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Автор</label>
              <input className={styles.input} name="author" value={form.author} onChange={handleChange} placeholder="Имя автора" list="persons-list" />
              <datalist id="persons-list">
                {persons.map((p) => <option key={p.id} value={p.name} />)}
              </datalist>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Год издания</label>
              <CustomSelect
                value={form.year}
                onChange={(val) => setForm((prev) => ({ ...prev, year: val }))}
                options={YEAR_OPTIONS}
                placeholder="Выберите год"
                searchable
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Язык</label>
              <CustomSelect
                value={form.language}
                onChange={(val) => setForm((prev) => ({ ...prev, language: val }))}
                options={LANGUAGE_OPTIONS}
                placeholder="Выберите язык"
              />
            </div>

            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Описание</label>
              <textarea className={styles.textarea} name="description" value={form.description} onChange={handleChange} placeholder="Краткое описание книги..." />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.grid}>
            <div className={`${styles.field} ${styles.fullWidth}`}>
              <label className={styles.label}>Жанры</label>
              <MultiSelect
                value={selectedGenres}
                onChange={setSelectedGenres}
                options={allGenres}
                placeholder="Выберите жанры..."
              />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label}>Обложка</label>
              <div className={styles.imagePicker}>
                {isEdit && (
                  <div className={styles.imagePreviewBox}>
                    {imagePreview ? (
                      <img src={imagePreview} alt="Обложка" />
                    ) : (
                      <div className={styles.imagePlaceholder}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                )}
                <button type="button" className={styles.imagePickButton}>
                  Выбрать обложку
                  <input type="file" accept="image/*" className={styles.fileInput} onChange={handleImageChange} />
                </button>
              </div>
            </div>

            <div className={styles.fileField}>
              <label className={styles.label}>Файл книги (PDF/EPUB)</label>
              <div className={styles.dropzone}>
                <span className={styles.dropzoneIcon}>📄</span>
                <div className={styles.dropzoneText}>
                  <span className={styles.dropzoneTitle}>{bookFile ? bookFile.name : 'Выберите файл книги'}</span>
                  <span className={styles.dropzoneHint}>PDF или EPUB</span>
                </div>
                <input type="file" accept=".pdf,.epub" className={styles.fileInput} onChange={(e) => setBookFile(e.target.files[0])} />
              </div>
            </div>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="submit" className={styles.saveBtn} disabled={saving}>
            {saving ? 'Сохранение...' : (isEdit ? 'Сохранить' : 'Добавить книгу')}
          </button>
          <Link to="/books" className={styles.cancelBtn}>Отмена</Link>
        </div>
      </form>
    </div>
  );
};

export default BookForm;
